import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment/moment'
import TableItems from '../Table/TableItems'
import OrganizationStatus from '../UI/OrganizationStatus'
import IconChevron from '../Icons/IconChevron'
import { OrganizationTypes } from '../../interfaces/organization'
import { CsvColumn } from '../../utils/toCsv'
import { isProvisionalPeriod } from '../../helpers/isProvisionalPeriod'
import {
  sortDateNullsLast,
  sortNumericNullsLast,
  sortTextNullsLast,
} from '../../utils/tableSort'

const DEFAULT_SORT = [{ id: 'id', desc: true }]

interface Props {
  isLoading: boolean
  organizations: OrganizationTypes[]
}

interface OriginalTypes {
  cell: { row: { original: OrganizationTypes } }
}

const formatEod = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YY') : '-'

// Shared by the search box and the CSV export so both describe the same rows.
export const organizationCsvColumns = (
  t: (key: string) => string,
): CsvColumn<OrganizationTypes>[] => [
  { header: 'ID', value: (row) => row.id },
  { header: t('companyNameColumn'), value: (row) => row.company_name ?? '' },
  { header: t('companyEmail'), value: (row) => row.company_email ?? '' },
  {
    header: t('status'),
    value: (row) => (row.status === 'active' ? t('active') : t('pending')),
  },
  {
    header: t('subscriptionEod'),
    // The export carries the caveat in words - it has no footnote to lean on,
    // and a date read out of this file is exactly where a provisional period
    // would be mistaken for a real one.
    value: (row) => {
      const eod = formatEod(row.subscription_end_date)
      if (eod === '-' || !isProvisionalPeriod(row.status)) return eod
      return `${eod} (${t('provisionalPeriod')})`
    },
  },
]

const OrganizationsList = ({ isLoading, organizations = [] }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // The footnote only earns its space when a row actually carries an asterisk.
  const hasProvisionalPeriod = organizations.some(
    ({ status, subscription_end_date: eod }) =>
      eod && isProvisionalPeriod(status),
  )

  const columns = [
    {
      Header: 'ID',
      accessor: 'id',
      sortType: sortNumericNullsLast,
    },
    {
      Header: t('companyNameColumn'),
      accessor: 'company_name',
      sortType: sortTextNullsLast,
      Cell: ({
        value: companyName,
      }: {
        value: OrganizationTypes['company_name']
      }) =>
        companyName ? (
          <span className='text-black/80'>{companyName}</span>
        ) : (
          '-'
        ),
    },
    {
      Header: t('companyEmail'),
      accessor: 'company_email',
      sortType: sortTextNullsLast,
      Cell: ({
        value: companyEmail,
      }: {
        value: OrganizationTypes['company_email']
      }) =>
        companyEmail ? (
          <a className='hover:underline' href={`mailto:${companyEmail}`}>
            {companyEmail}
          </a>
        ) : (
          '-'
        ),
    },
    {
      Header: t('status'),
      accessor: 'status',
      sortType: sortTextNullsLast,
      Cell: ({ value: status }: { value: OrganizationTypes['status'] }) => (
        <OrganizationStatus status={status} />
      ),
    },
    {
      Header: t('subscriptionEod'),
      accessor: 'subscription_end_date',
      sortType: sortDateNullsLast,
      // Reads the row rather than the value alone: whether the date is an
      // expiry or a placeholder is decided by the row's status.
      Cell: ({
        cell: {
          row: { original },
        },
      }: OriginalTypes) => {
        const eod = formatEod(original.subscription_end_date)
        return (
          <span>
            {eod}
            {eod !== '-' && isProvisionalPeriod(original.status) && (
              <span className='text-lightGray'>*</span>
            )}
          </span>
        )
      },
    },
    {
      id: 1,
      Header: ' ',
      disableSortBy: true,
      Cell: ({
        cell: {
          row: { original },
        },
      }: OriginalTypes) => (
        <button
          onClick={() => navigate(`/super-admin/organizations/${original.id}`)}
          type='button'
          className='flex justify-center items-center mx-auto rounded-full w-8 h-8  hover:bg-gray-100 duration-300 transition-colors'
        >
          <IconChevron />
        </button>
      ),
    },
  ]

  return (
    <div className='w-full min-h-[710px] px-5 py-5 md:px-10 md:py-6 lg:px-20 lg:py-11 bg-white rounded-20px shadow-form mt-7'>
      <TableItems
        columns={columns}
        data={organizations}
        isDataLoading={isLoading}
        noDataFoundMessage='noOrganizationsFound'
        showSearch={false}
        isSortable
        // Newest first: the backend hands these over oldest-first for
        // organisations and by charge date for payments, and neither is what a
        // super admin opening the list wants to see at the top.
        defaultSortBy={DEFAULT_SORT}
        pageSize={6}
        tableHeightClassName='h-[660px]'
      />
      {hasProvisionalPeriod && (
        <p className='mt-4 text-sm text-lightGray'>
          * {t('provisionalPeriodNote')}
        </p>
      )}
    </div>
  )
}

export default OrganizationsList
